import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const fieldCls = "bg-slate-900 border-slate-700 text-slate-100";
const labelCls = "text-xs uppercase tracking-[0.18em] text-slate-400 font-mono";

export const EditSiteDialog = ({ site, groups, onClose, onSaved, onDeleted }) => {
  const [form, setForm] = useState({ title: "", url: "", description: "", image_url: "", group_id: "none" });
  const [saving, setSaving] = useState(false);
  const isNew = site && !site.id;

  useEffect(() => {
    if (site) setForm({ title: site.title, url: site.url, description: site.description, image_url: site.image_url || "",
      group_id: groups.some((g) => g.id === site.group_id) ? site.group_id : "none" });
  }, [site, groups]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    let url = form.url.trim();
    if (url && !/^https?:\/\//i.test(url)) url = `https://${url}`;
    setSaving(true);
    try {
      const body = { ...form, url, image_url: form.image_url.trim(), group_id: form.group_id === "none" ? null : form.group_id };
      const { data } = isNew ? await api.post("/sites", body) : await api.put(`/sites/${site.id}`, body);
      onSaved(data);
      toast.success(isNew ? "Sito aggiunto" : "Sito aggiornato");
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare "${site.title}"?`)) return;
    try {
      const { data } = await api.delete(`/sites/${site.id}`);
      onDeleted(data);
      toast.success("Sito eliminato");
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={!!site} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-[#111827] border-slate-700 text-slate-100 sm:max-w-lg" data-testid="edit-tile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isNew ? "Nuovo sito" : `Modifica sito ${site?.position}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <label className="block space-y-1.5"><span className={labelCls}>Titolo</span>
            <Input value={form.title} onChange={set("title")} maxLength={80} className={fieldCls} data-testid="edit-tile-title-input" />
          </label>
          <label className="block space-y-1.5"><span className={labelCls}>Link</span>
            <Input value={form.url} onChange={set("url")} placeholder="https://esempio.it" className={`${fieldCls} font-mono`} data-testid="edit-tile-url-input" />
          </label>
          <label className="block space-y-1.5"><span className={labelCls}>Descrizione ({form.description.length}/200)</span>
            <Textarea value={form.description} onChange={set("description")} maxLength={200} rows={3} className={fieldCls} data-testid="edit-tile-desc-input" />
          </label>
          <label className="block space-y-1.5"><span className={labelCls}>Sezione</span>
            <Select value={form.group_id} onValueChange={(v) => setForm((f) => ({ ...f, group_id: v }))}>
              <SelectTrigger className={fieldCls} data-testid="edit-tile-group-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                <SelectItem value="none" data-testid="edit-tile-group-none">Senza sezione</SelectItem>
                {groups.map((g) => <SelectItem key={g.id} value={g.id} data-testid={`edit-tile-group-${g.id}`}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="block space-y-1.5"><span className={labelCls}>Immagine anteprima (opzionale)</span>
            <Input value={form.image_url} onChange={set("image_url")} placeholder="Vuoto = screenshot automatico" className={`${fieldCls} font-mono`} data-testid="edit-tile-image-input" />
          </label>
        </div>
        <DialogFooter className="gap-2">
          {!isNew && (
            <Button variant="ghost" onClick={remove} className="sm:mr-auto text-rose-400 hover:bg-rose-500/10 hover:text-rose-300" data-testid="edit-tile-delete-button">
              <Trash2 className="w-4 h-4 mr-1.5" /> Elimina
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} className="text-slate-400 hover:bg-slate-800 hover:text-white" data-testid="edit-tile-cancel-button">Annulla</Button>
          <Button onClick={save} disabled={saving || !form.title.trim()} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="edit-tile-save-button">
            {saving ? "Salvataggio…" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
